// src/components/PendingOrdersPanel.js
//
// 🕒 الأوردرات المعلقة — أداة تعديل سريع للتيست (بديل عن تعديل Firebase/
// Supabase يدويًا). بتقرأ/تعدّل `daf_confirmation.pending_orders_api` عبر
// راوتات السوبر أدمن الجديدة في الـ Shopify-app Express server:
//   GET  /api/super-admin/pendingOrders?brand=<اختياري>
//   POST /api/super-admin/pendingOrders/updateTime  { docId, created_at?, follow_up_count?, expire_at? }
// كل الطلبات بتحمل GoTrue access token (عبر superAdminFetch — نفس آلية
// زرار "ادخل البراند" في SuperAdminDashboard.js). 🔄 المرحلة الأخيرة
// (GoTrue-login-cutover، 2026-07-06): كان بيستخدم Firebase ID token مباشرة،
// اتنقل لـ superAdminFetch المشترك.
//
// ⚠️ ليه بنعدّل created_at تحديدًا: workflow n8n "2 check_the_time..." بيقرر
// امتى يبعت رسالة المتابعة/يلغي الأوردر تلقائيًا بناءً على `data->>'created_at'`
// (مش أي عمود typed) — تحديد تاريخ أقدم = الأوردر يبقى "مستحق" فورًا، فبيوفر
// وقت انتظار حقيقي وقت التيست.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { superAdminFetch } from '../utils/superAdminApi';
import './PendingOrdersPanel.css';

// ISO-8601 (UTC) -> قيمة مناسبة لـ <input type="datetime-local"> (بتوقيت المتصفح المحلي)
function isoToLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// قيمة <input type="datetime-local"> (محلي) -> ISO-8601 (UTC)، أو null لو غير صالح
function localInputValueToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function formatDisplayDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' });
}

function PendingOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text }
  const [brandFilter, setBrandFilter] = useState('all');
  const [drafts, setDrafts] = useState({}); // docId -> { createdAt: <local input value>, followUpCount: number|string }
  const [savingDocId, setSavingDocId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await superAdminFetch('/pendingOrders');
      setOrders(data.orders || []);
      setDrafts((prev) => {
        const next = { ...prev };
        (data.orders || []).forEach((order) => {
          next[order.docId] = {
            createdAt: isoToLocalInputValue(order.createdAt),
            followUpCount: order.followUpCount ?? 0,
            expireAt: isoToLocalInputValue(order.expireAt),
          };
        });
        return next;
      });
    } catch (err) {
      console.error('Error fetching pending orders:', err);
      setError(err.message || 'حدث خطأ غير متوقع أثناء تحميل الأوردرات المعلقة');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const brandOptions = useMemo(() => {
    const set = new Set(orders.map((o) => o.brandName).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (brandFilter === 'all') return orders;
    return orders.filter((o) => o.brandName === brandFilter);
  }, [orders, brandFilter]);

  const updateDraft = (docId, patch) => {
    setDrafts((prev) => ({ ...prev, [docId]: { ...prev[docId], ...patch } }));
  };

  const saveOrderTiming = async (docId, patch, successNote) => {
    setSavingDocId(docId);
    setMessage(null);
    try {
      const data = await superAdminFetch('/pendingOrders/updateTime', {
        method: 'POST',
        body: JSON.stringify({ docId, ...patch }),
      });
      setMessage({
        type: 'success',
        text: successNote || `✅ تم تحديث الأوردر ${data.order?.orderNumber || docId} بنجاح`,
      });
      await fetchOrders();
    } catch (err) {
      console.error('Error updating pending order timing:', err);
      setMessage({ type: 'error', text: `❌ ${err.message || 'حدث خطأ غير متوقع أثناء الحفظ'}` });
    } finally {
      setSavingDocId(null);
    }
  };

  const handleSaveRow = (order) => {
    const draft = drafts[order.docId] || {};
    const patch = {};

    const createdAtIso = localInputValueToIso(draft.createdAt);
    if (draft.createdAt && !createdAtIso) {
      setMessage({ type: 'error', text: '❌ تاريخ الإنشاء غير صالح' });
      return;
    }
    if (createdAtIso) patch.created_at = createdAtIso;

    if (draft.followUpCount !== '' && draft.followUpCount !== undefined && draft.followUpCount !== null) {
      const n = Number(draft.followUpCount);
      if (!Number.isInteger(n) || n < 0) {
        setMessage({ type: 'error', text: '❌ عدد مرات المتابعة يجب أن يكون رقمًا صحيحًا ≥ 0' });
        return;
      }
      patch.follow_up_count = n;
    }

    const expireAtIso = localInputValueToIso(draft.expireAt);
    if (draft.expireAt && !expireAtIso) {
      setMessage({ type: 'error', text: '❌ وقت الإلغاء (expire_at) غير صالح' });
      return;
    }
    if (expireAtIso) patch.expire_at = expireAtIso;

    if (Object.keys(patch).length === 0) {
      setMessage({ type: 'error', text: '❌ لا يوجد تغيير للحفظ' });
      return;
    }

    saveOrderTiming(order.docId, patch);
  };

  const handleQuickBackdate = (order, ms, label) => {
    const newIso = new Date(Date.now() - ms).toISOString();
    updateDraft(order.docId, { createdAt: isoToLocalInputValue(newIso) });
    saveOrderTiming(
      order.docId,
      { created_at: newIso },
      `✅ الأوردر ${order.orderNumber || order.docId} بقى ${label} — هيتاخد كـ"مستحق" فورًا`
    );
  };

  // 🚫 يجعل الأوردر مستحقًا للإلغاء التلقائي فورًا: follow_up_count=1 (أول رسالة
  // "اتبعتت") + expire_at في الماضي (الآن ناقص دقيقة). فرع الـ auto-cancel في n8n
  // بيقرأ (follow_up_count=1 AND expire_at<=now) — مش created_at — فبكده يتلغى في
  // أول تشغيلة تالية للـ check_time بدل انتظار ساعات الـ cancel_timeout.
  const handleMakeCancelDue = (order) => {
    const dueIso = new Date(Date.now() - 60 * 1000).toISOString();
    updateDraft(order.docId, {
      expireAt: isoToLocalInputValue(dueIso),
      followUpCount: 1,
    });
    saveOrderTiming(
      order.docId,
      { expire_at: dueIso, follow_up_count: 1 },
      `✅ الأوردر ${order.orderNumber || order.docId} بقى مستحقًا للإلغاء التلقائي — هيتلغى في تشغيلة check_time الجاية`
    );
  };

  return (
    <div className="pending-orders-container">
      <div className="pending-orders-header">
        <h2>
          🕒 الأوردرات المعلقة
          <span className="pending-orders-count-badge">{filteredOrders.length}</span>
        </h2>
        <div className="pending-orders-controls">
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="pending-orders-brand-select"
          >
            <option value="all">كل البراندات</option>
            {brandOptions.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
          <button onClick={fetchOrders} className="pending-orders-refresh-btn" disabled={loading}>
            {loading ? '⏳ جاري التحميل...' : '🔄 تحديث'}
          </button>
        </div>
      </div>

      <p className="pending-orders-hint">
        عدّل <strong>وقت الإنشاء (created_at)</strong> عشان تسرّع <em>أول رسالة</em>، أو{' '}
        <strong>وقت الإلغاء (expire_at)</strong> عشان تسرّع <em>الإلغاء التلقائي</em> (auto-cancel) — بديل
        عن الدخول لـ Firebase/Supabase يدويًا. مهم: الإلغاء التلقائي بيعتمد على <code>expire_at</code>{' '}
        (اللي = وقت أول رسالة + ساعات المهلة)، <u>مش</u> على <code>created_at</code>. عشان تختبره فورًا
        استعمل زرار <strong>«🚫 إلغاء مستحق الآن»</strong> — بيظبط follow_up_count=1 و expire_at في
        الماضي، فالأوردر يتلغى في أول تشغيلة تالية للـ check_time (كل 15 دقيقة).
      </p>

      {message && <div className={`pending-orders-message ${message.type}`}>{message.text}</div>}

      {error && <div className="pending-orders-message error">❌ {error}</div>}

      {loading && orders.length === 0 ? (
        <div className="pending-orders-loading">⏳ جاري تحميل الأوردرات المعلقة...</div>
      ) : filteredOrders.length === 0 ? (
        <div className="pending-orders-loading">لا توجد أوردرات معلّقة حاليًا.</div>
      ) : (
        <div className="pending-orders-table-wrapper">
          <table className="pending-orders-table">
            <thead>
              <tr>
                <th>البراند</th>
                <th>رقم الأوردر</th>
                <th>العميل</th>
                <th>الهاتف</th>
                <th>وقت الإنشاء (created_at)</th>
                <th>مرات المتابعة</th>
                <th>وقت الإلغاء (expire_at)</th>
                <th>إجراءات سريعة</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => {
                const draft = drafts[order.docId] || {};
                const isSaving = savingDocId === order.docId;
                return (
                  <tr key={order.docId} className="pending-orders-row">
                    <td>
                      <strong className="pending-orders-brand">{order.brandName || '—'}</strong>
                    </td>
                    <td>{order.orderNumber || '—'}</td>
                    <td>{order.customerName || '—'}</td>
                    <td>{order.customerPhone || '—'}</td>
                    <td>
                      <input
                        type="datetime-local"
                        value={draft.createdAt || ''}
                        onChange={(e) => updateDraft(order.docId, { createdAt: e.target.value })}
                        className="pending-orders-input"
                        disabled={isSaving}
                      />
                      <div className="pending-orders-subvalue">حاليًا: {formatDisplayDate(order.createdAt)}</div>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={draft.followUpCount ?? 0}
                        onChange={(e) => updateDraft(order.docId, { followUpCount: e.target.value })}
                        className="pending-orders-input pending-orders-input--narrow"
                        disabled={isSaving}
                      />
                    </td>
                    <td>
                      <input
                        type="datetime-local"
                        value={draft.expireAt || ''}
                        onChange={(e) => updateDraft(order.docId, { expireAt: e.target.value })}
                        className="pending-orders-input"
                        disabled={isSaving}
                      />
                      <div className="pending-orders-subvalue">حاليًا: {formatDisplayDate(order.expireAt)}</div>
                    </td>
                    <td>
                      <div className="pending-orders-quick-actions">
                        <button
                          onClick={() => handleQuickBackdate(order, 60 * 60 * 1000, 'قديم بساعة')}
                          disabled={isSaving}
                          className="pending-orders-quick-btn"
                          title="اجعل created_at = الآن ناقص ساعة، واحفظ فورًا"
                        >
                          ⏪ ساعة
                        </button>
                        <button
                          onClick={() => handleQuickBackdate(order, 24 * 60 * 60 * 1000, 'قديم بيوم')}
                          disabled={isSaving}
                          className="pending-orders-quick-btn"
                          title="اجعل created_at = الآن ناقص يوم، واحفظ فورًا"
                        >
                          ⏪ يوم
                        </button>
                        <button
                          onClick={() => handleMakeCancelDue(order)}
                          disabled={isSaving}
                          className="pending-orders-quick-btn pending-orders-quick-btn--danger"
                          title="follow_up_count=1 + expire_at=الآن، فيتلغى تلقائيًا في تشغيلة check_time الجاية"
                        >
                          🚫 إلغاء مستحق الآن
                        </button>
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => handleSaveRow(order)}
                        disabled={isSaving}
                        className="pending-orders-save-btn"
                      >
                        {isSaving ? '⏳ جاري الحفظ...' : '💾 احفظ'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PendingOrdersPanel;
